'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import styles from './styles.module.css'
import { getStrapiImageUrl } from '@/lib/strapi'

interface Article {
  id: number
  title: string
  slug: string
  shortDescription?: string
  featuredImage?: {
    url: string
    alternativeText?: string
  }
  isExternal?: boolean
  externalUrl?: string
}

interface RelatedArticlesProps {
  articles: Article[]
  currentSlug: string
}

const RelatedArticles: React.FC<RelatedArticlesProps> = ({ articles, currentSlug }) => {
  const [visibleCount, setVisibleCount] = useState<number>(3)

  // Фильтруем текущую статью
  const filteredArticles = articles.filter(article => article.slug !== currentSlug)
  
  // Берём только видимые статьи
  const visibleArticles = filteredArticles.slice(0, visibleCount)
  const hasMore = filteredArticles.length > visibleCount

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 3)
  }

  if (filteredArticles.length === 0) {
    return null
  }

  return (
    <section className={styles.relatedSection}>
      <div className={styles.header}>
        <p className={styles.subtitle}>Блог</p>
        <h2 className={styles.title}>Ещё больше экспертных статей в нашем блоге</h2>
      </div>

      <div className={styles.grid}>
        {visibleArticles.map((article) => {
          const isExternal = article.isExternal && article.externalUrl

          const CardContent = (
            <>
              <div className={styles.imageContainer}>
                <div className={styles.imageBackground}>
                  <Image
                    src={article.featuredImage?.url 
                      ? getStrapiImageUrl(article.featuredImage.url) 
                      : '/images/blog/default.jpg'
                    }
                    alt={article.featuredImage?.alternativeText || article.title}
                    fill
                    className={styles.image}
                    sizes="(max-width: 767px) 100vw, (max-width: 1439px) 10rem, 21.875rem"
                  />
                </div>
              </div>
              <div className={styles.content}>
                <h3 className={styles.cardTitle}>{article.title}</h3>
                {article.shortDescription && (
                  <p className={styles.cardDescription}>{article.shortDescription}</p>
                )}
                <div className={styles.link}>
                  <span className={styles.linkText}>Подробнее</span>
                  <div className={styles.linkIcon}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z" fill="#f23b67" />
                    </svg>
                  </div>
                </div>
              </div>
            </>
          )

          return isExternal ? (
            <a
              key={article.id}
              href={article.externalUrl!}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.card}
            >
              {CardContent}
            </a>
          ) : (
            <Link 
              key={article.id} 
              href={`/articles/${article.slug}`}
              className={styles.card}
            >
              {CardContent}
            </Link>
          )
        })}
      </div>

      {hasMore && (
        <button className={styles.loadMoreButton} onClick={handleLoadMore}>
          <span className={styles.loadMoreText}>Показать ещё</span>
          <svg className={styles.loadMoreIcon} width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.94 0L4 3.05333L7.06 0L8 0.94L4 4.94L0 0.94L0.94 0Z" fill="white"/>
          </svg>
        </button>
      )}
    </section>
  )
}

export default RelatedArticles
